import { useEffect, useMemo, useState } from "react";
import { api, API } from "@/lib/api";
import { Area, AreaChart, Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Sparkles, Download } from "lucide-react";
import { toast } from "sonner";
import { TriggerCard } from "@/components/TriggerCard";

export default function Insights() {
  const [range, setRange] = useState(7);
  const [data, setData] = useState([]);
  const [patterns, setPatterns] = useState([]);
  const [stats, setStats] = useState({});

  useEffect(() => {
    (async () => {
      const { data: logs } = await api.get(`/checkins?days=${range}`);
      setData(logs);
      const { data: p } = await api.get("/insights/patterns");
      setPatterns(p.patterns || []);
      setStats({ avg_sleep: p.avg_sleep, avg_energy: p.avg_energy });
    })();
  }, [range]);

  const chartData = useMemo(
    () =>
      data.map((d) => ({
        date: d.date.slice(5),
        sleep: d.sleep_quality,
        energy: d.energy,
        hotFlashes: d.hot_flashes,
      })),
    [data]
  );

  return (
    <div className="rise">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-[0.22em] text-[#8C7A70] font-semibold">
            Your rhythms
          </p>
          <h1 className="mt-2 font-serif-display text-4xl sm:text-5xl text-[#2C221E] leading-tight" data-testid="insights-title">
            Insights
          </h1>
          <p className="mt-3 text-[#52443E] max-w-2xl">
            Not diagnostic — reassuring. The shapes of your days, surfaced gently so you can see what has been happening.
          </p>
        </div>
        <div className="inline-flex items-center gap-3 flex-wrap">
          <button
            onClick={async () => {
              try {
                const resp = await fetch(`${API}/export/pdf`, { credentials: "include" });
                if (!resp.ok) throw new Error("export failed");
                const blob = await resp.blob();
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = "meridia-summary.pdf";
                document.body.appendChild(a);
                a.click();
                a.remove();
                URL.revokeObjectURL(url);
                toast.success("Your 90-day summary is downloading.");
              } catch {
                toast.error("Couldn't build the PDF just now.");
              }
            }}
            className="btn-ghost inline-flex items-center gap-2"
            data-testid="insights-export-pdf"
          >
            <Download size={14} /> Doctor's PDF
          </button>
          <div className="inline-flex rounded-full border border-[#D8CBB9] bg-[#FFFDF9] p-1">
            {[7, 30].map((r) => (
              <button
                key={r}
                onClick={() => setRange(r)}
                className={`px-4 py-2 rounded-full text-sm transition-colors ${
                  range === r ? "bg-[#2C221E] text-[#FAF6F0]" : "text-[#52443E]"
                }`}
                data-testid={`insights-range-${r}`}
              >
                {r === 7 ? "Last 7 days" : "Last 30 days"}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-10 grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 card-paper p-6 sm:p-8" data-testid="insights-sleep-energy-card">
          <h2 className="font-serif-display text-xl text-[#2C221E]">Sleep & energy</h2>
          <p className="text-sm text-[#73635B]">How well you slept against how alive you felt.</p>
          <div className="mt-6 h-72">
            {chartData.length === 0 ? (
              <div className="h-full flex items-center justify-center text-sm text-[#8C7A70]">
                A few check-ins and the pattern will begin to show.
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData}>
                  <defs>
                    <linearGradient id="s" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#5F7463" stopOpacity={0.5} />
                      <stop offset="95%" stopColor="#5F7463" stopOpacity={0.05} />
                    </linearGradient>
                    <linearGradient id="e" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#D4A359" stopOpacity={0.5} />
                      <stop offset="95%" stopColor="#D4A359" stopOpacity={0.05} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke="#EAE0D2" strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="date" stroke="#8C7A70" tick={{ fontSize: 12 }} />
                  <YAxis domain={[0, 5]} stroke="#8C7A70" tick={{ fontSize: 12 }} />
                  <Tooltip
                    contentStyle={{ background: "#FFFDF9", border: "1px solid #E5D9C8", borderRadius: 12 }}
                    labelStyle={{ color: "#2C221E" }}
                  />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Area type="monotone" dataKey="sleep" name="Sleep" stroke="#5F7463" fill="url(#s)" strokeWidth={2} />
                  <Area type="monotone" dataKey="energy" name="Energy" stroke="#D4A359" fill="url(#e)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        <div className="card-paper p-6 sm:p-8" data-testid="insights-flashes-card">
          <h2 className="font-serif-display text-xl text-[#2C221E]">Hot flashes</h2>
          <p className="text-sm text-[#73635B]">Daily count.</p>
          <div className="mt-6 h-72">
            {chartData.length === 0 ? (
              <div className="h-full flex items-center justify-center text-sm text-[#8C7A70]">
                Not enough data yet.
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData}>
                  <CartesianGrid stroke="#EAE0D2" strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="date" stroke="#8C7A70" tick={{ fontSize: 12 }} />
                  <YAxis stroke="#8C7A70" tick={{ fontSize: 12 }} allowDecimals={false} />
                  <Tooltip
                    contentStyle={{ background: "#FFFDF9", border: "1px solid #E5D9C8", borderRadius: 12 }}
                  />
                  <Bar dataKey="hotFlashes" name="Flashes" fill="#C26D53" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>

      <div className="mt-6 grid md:grid-cols-3 gap-6">
        <div className="card-paper p-6" data-testid="insights-avg-sleep">
          <p className="text-xs uppercase tracking-widest text-[#8C7A70] font-semibold">Avg sleep</p>
          <p className="mt-2 font-serif-display text-4xl text-[#2C221E]">{stats.avg_sleep ?? "—"}</p>
          <p className="text-sm text-[#73635B]">out of 5</p>
        </div>
        <div className="card-paper p-6" data-testid="insights-avg-energy">
          <p className="text-xs uppercase tracking-widest text-[#8C7A70] font-semibold">Avg energy</p>
          <p className="mt-2 font-serif-display text-4xl text-[#2C221E]">{stats.avg_energy ?? "—"}</p>
          <p className="text-sm text-[#73635B]">out of 5</p>
        </div>
        <div className="card-paper p-6" data-testid="insights-entries-count">
          <p className="text-xs uppercase tracking-widest text-[#8C7A70] font-semibold">Entries</p>
          <p className="mt-2 font-serif-display text-4xl text-[#2C221E]">{data.length}</p>
          <p className="text-sm text-[#73635B]">in the last {range} days</p>
        </div>
      </div>

      <div className="mt-10 card-paper p-6 sm:p-8" data-testid="insights-patterns-card">
        <div className="flex items-center gap-2">
          <Sparkles size={18} className="text-[#C26D53]" />
          <h2 className="font-serif-display text-2xl text-[#2C221E]">Gentle observations</h2>
        </div>
        <ul className="mt-5 space-y-4">
          {patterns.map((p, i) => (
            <li key={i} className="text-[#52443E] leading-relaxed" data-testid={`insights-pattern-${i}`}>
              <span className="text-[#C26D53] mr-2">—</span>
              {p}
            </li>
          ))}
        </ul>
      </div>

      <TriggerCard />
    </div>
  );
}
